import React from 'react';
import { Box, Button, Card, CardContent, CardHeader, Chip, Divider, Stack, Typography } from '@mui/material';
import { PricingDiscountTypes } from '../../types';
import { calculateDiscountedPrice, formatDate, safeParseFloat, formatCurrencyDisplay } from '../../utils';
import { KEY_CHARGE_ONE_TIME_PAYMENT } from '../../constants';
import Countdown from './Countdown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventIcon from '@mui/icons-material/Event';

interface CartItemCardProps {
    item: any; // Using any due to incomplete type definitions
    onRemove: () => void;
    onExpired?: (itemId: number, eventName: string, childName: string) => void;
}

const CartItemCard: React.FC<CartItemCardProps> = ({ item, onRemove, onExpired }) => {
    const depositAmount = safeParseFloat(item.item?.depositAmount);
    const totalAmount = safeParseFloat(item.item?.chargePerPeriod);
    const remainingAmount = totalAmount - depositAmount;
    
    const discountedDeposit = item.discountAmountDeposit 
        ? depositAmount - safeParseFloat(item.discountAmountDeposit)
        : depositAmount;
    
    const discountedTotal = item.discountAmountCoupon
        ? totalAmount - safeParseFloat(item.discountAmountCoupon)
        : totalAmount;
    
    const hasPayInFullDiscount = item.item?.discountType !== undefined && 
        item.item?.discountType !== PricingDiscountTypes.DISABLED;
    
    const payInFullAmount = hasPayInFullDiscount
        ? safeParseFloat(calculateDiscountedPrice(
            item.item?.chargePerPeriod,
            item.item?.discountType,
            item.item?.discountAmount,
            item.discountAmountCoupon
        ))
        : discountedTotal;
    
    // Calculate savings for pay-in-full option
    const payInFullSavings = hasPayInFullDiscount && payInFullAmount < discountedTotal
        ? discountedTotal - payInFullAmount
        : 0;

    // Get selected days (stored directly on the cart item, not item.item)
    const daysMap = {
        sunday: 'Sun',
        monday: 'Mon',
        tuesday: 'Tue',
        wednesday: 'Wed',
        thursday: 'Thu',
        friday: 'Fri',
        saturday: 'Sat',
    };

    const selectedDays = Object.entries(daysMap)
        .filter(([key]) => item[key] === true)
        .map(([_, abbrev]) => abbrev);

    return (
        <Card
            elevation={2}
            sx={{
                mb: 2,
                borderLeft: '4px solid #88B73E',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
            }}
        >
            <CardHeader
                title={item.item?.event?.name || 'Event'}
                subheader={item.item?.event?.location?.name ?? ''}
                action={item.heldUntil ? <Countdown 
                    heldUntil={item.heldUntil} 
                    onExpire={onExpired ? () => onExpired(
                        item.id,
                        item.item?.event?.name || 'Event',
                        `${item.child?.firstName} ${item.child?.lastName}`
                    ) : undefined}
                /> : null}
                titleTypographyProps={{ variant: 'h5', fontWeight: 600, fontSize: '1.4rem' }}
                subheaderTypographyProps={{ variant: 'body1', color: 'text.secondary', fontSize: '0.95rem' }}
                sx={{
                    backgroundColor: 'rgba(136, 183, 62, 0.08)',
                    borderBottom: '1px solid rgba(136, 183, 62, 0.2)',
                    py: 1.75,
                }}
            />
            <CardContent sx={{ p: 2.5 }}>
                <Stack spacing={1.75}>
                    {/* Event Dates */}
                    {item.item?.event?.startDate && item.item?.event?.endDate && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarTodayIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                                {formatDate(item.item.event.startDate)} - {formatDate(item.item.event.endDate)}
                            </Typography>
                        </Box>
                    )}

                    {/* Selected Days */}
                    {selectedDays.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <EventIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.75, fontSize: '0.85rem' }}>
                                Selected Days:
                            </Typography>
                            {selectedDays.map((day) => (
                                <Chip
                                    key={day}
                                    label={day}
                                    size="small"
                                    sx={{
                                        backgroundColor: 'rgba(136, 183, 62, 0.15)',
                                        color: 'success.main',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        height: 22,
                                    }}
                                />
                            ))}
                        </Box>
                    )}

                    {/* Payment Options Section */}
                    {(hasPayInFullDiscount && payInFullAmount < discountedTotal) || depositAmount > 0 ? (
                        <>
                            <Divider />
                            
                            {/* Pay In Full Today Section - Shown first to promote it */}
                            {hasPayInFullDiscount && payInFullAmount < discountedTotal && (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                                            Pay In Full Today
                                        </Typography>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                                                ${formatCurrencyDisplay(payInFullAmount)}
                                            </Typography>
                                            {payInFullSavings > 0 && (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', mt: 0.25 }}>
                                                    Save ${formatCurrencyDisplay(payInFullSavings)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    
                                    {/* OR Divider - Only show if deposit option exists */}
                                    {depositAmount > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
                                            <Divider sx={{ flex: 1 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#666',
                                                    fontWeight: 600,
                                                    px: 1,
                                                    fontSize: '0.85rem',
                                                }}
                                            >
                                                OR
                                            </Typography>
                                            <Divider sx={{ flex: 1 }} />
                                        </Box>
                                    )}
                                </>
                            )}

                            {/* Deposit Section - Only show if deposit option exists */}
                            {depositAmount > 0 && (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                                            Deposit Required
                                        </Typography>
                                        <Box sx={{ textAlign: 'right' }}>
                                            {item.discountAmountDeposit ? (
                                            <>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        textDecoration: 'line-through',
                                                        color: 'text.secondary',
                                                        fontSize: '0.95rem',
                                                    }}
                                                >
                                                    ${formatCurrencyDisplay(depositAmount)}
                                                </Typography>
                                                <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                                                    ${formatCurrencyDisplay(discountedDeposit)}
                                                </Typography>
                                            </>
                                            ) : (
                                            <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                                                ${formatCurrencyDisplay(depositAmount)}
                                            </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Remaining Balance Section */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                                                Remaining Balance
                                            </Typography>
                                            {item.item?.chargeType?.id === KEY_CHARGE_ONE_TIME_PAYMENT && item.item?.autoPaymentDate && (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                    Charged on {formatDate(item.item.autoPaymentDate)}
                                                </Typography>
                                            )}
                                            {item.item?.chargeType?.id !== KEY_CHARGE_ONE_TIME_PAYMENT && item.item?.chargeType?.description && (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                    {item.item.chargeType.description}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                                            ${formatCurrencyDisplay(remainingAmount)}
                                        </Typography>
                                    </Box>
                                </>
                            )}

                            <Divider />
                        </>
                    ) : (
                        <Divider />
                    )}

                    {/* Total Section - Show with strikethrough if pay-in-full discount exists */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                            Total Fee
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{ 
                                fontSize: '1.4rem',
                                fontWeight: 600,
                                textDecoration: (hasPayInFullDiscount && payInFullAmount < discountedTotal) ? 'line-through' : 'none',
                                color: (hasPayInFullDiscount && payInFullAmount < discountedTotal) ? 'text.secondary' : 'inherit',
                            }}
                        >
                            ${formatCurrencyDisplay(totalAmount)}
                        </Typography>
                    </Box>

                    {/* Remove Button */}
                    <Box sx={{ pt: 0.75 }}>
                        <Button
                            size="medium"
                            color="error"
                            onClick={onRemove}
                            sx={{ textTransform: 'none', fontSize: '0.9rem' }}
                        >
                            Remove from cart
                        </Button>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default CartItemCard;

